import { httpClient } from '../httpClient';

// Spec 024 — DPO purpose curation API helpers (FR-024-14 … FR-024-19).
// Backend routes live on the shared ROPA blueprint and are documented
// in `backend/modules/ropa/routes_purpose_curation.py`.
//
// Auth:
//   * GET endpoints (list, detail) + POST recluster — `dpo_oversight`
//     (DPO + Admin via `init_db.py` seed)
//   * POST accept/merge/reject — `curate_purpose_proposals` (DPO only;
//     seed lives in P5 backfill — tests create it inline on the fixture)
//
// Response envelopes:
//   * success_response → { data: { ... } }
//   * mutation_response → { message, data: { ... } }
//
// The shared httpClient returns { data: <body>, status }. Callers use
// the `unwrap()` helper in `usePurposeCuration.js` to peel both layers.

export const purposeCurationApi = {
  /**
   * List proposals with status filter + pagination.
   * @param {object} [params]
   * @param {string} [params.status]   Comma-separated statuses (default 'pending').
   *                                   Valid: pending,approved,merged,rejected,superseded.
   * @param {number} [params.limit]    Page size (default 50, max 200).
   * @param {number} [params.offset]   Starting index (default 0).
   * @param {string} [params.sort]     Sort spec (currently informational; backend
   *                                   sorts by -cluster_confidence, -created_at).
   * @param {string} [params.search]   Phase 5b — case-insensitive substring
   *                                   matched across proposed_name,
   *                                   proposed_business_unit, and
   *                                   proposed_description. Max 50 chars;
   *                                   no SQL wildcards (server-validated).
   */
  listProposals: (params) =>
    httpClient.get('/ropa/purpose-curation/proposals', { params }),

  /**
   * Fetch one proposal with full member_evidence + resolved_purpose summary.
   * @param {number|string} id  Proposal id.
   */
  getProposal: (id) =>
    httpClient.get(`/ropa/purpose-curation/proposals/${id}`),

  /**
   * Accept a proposal — creates a `ProcessingPurpose` and links its
   * member classifications. Server also auto-creates a ROPA record per
   * P2 (T053-T065).
   * @param {number|string} id
   * @param {object} body
   * @param {string} body.name          Required.
   * @param {string} [body.description]
   * @param {string} [body.legal_basis]    One of the 7 Saudi IR Art. 5 bases.
   * @param {string} [body.business_unit]
   */
  acceptProposal: (id, body) =>
    httpClient.post(`/ropa/purpose-curation/proposals/${id}/accept`, body || {}),

  /**
   * Merge a proposal into an existing `ProcessingPurpose`.
   * @param {number|string} id
   * @param {object} body
   * @param {number} body.target_ropa_id  Required.
   */
  mergeProposal: (id, body) =>
    httpClient.post(`/ropa/purpose-curation/proposals/${id}/merge`, body || {}),

  /**
   * Reject a proposal with a mandatory reason (min 10 chars per backend
   * schema). Leaves member classifications unlinked so a future recluster
   * can re-propose them.
   * @param {number|string} id
   * @param {object} body
   * @param {string} body.rejection_reason  Required, 10..2000 chars.
   */
  rejectProposal: (id, body) =>
    httpClient.post(`/ropa/purpose-curation/proposals/${id}/reject`, body || {}),

  /**
   * Trigger clustering for the caller's org. Returns:
   *   { proposals_created, proposals_updated, proposals_skipped_collision }
   * @param {object} [body]
   * @param {number} [body.organization_id]  Admin-only override.
   */
  recluster: (body) =>
    httpClient.post('/ropa/purpose-curation/recluster', body || {}),

  /**
   * Unclustered proposals grouped under their parent DataSource, ordered
   * by remnant size desc. Powers the "Group by data source" accordion.
   */
  unclusteredBySource: () =>
    httpClient.get('/ropa/purpose-curation/unclustered-by-source'),

  /**
   * Absorb a data source's entire unclustered remnant into one draft
   * purpose + draft ROPA.
   * @param {number} dataSourceId
   * @param {object} body
   * @param {string} body.name                  Required.
   * @param {string} [body.name_ar]
   * @param {string} [body.legal_basis]         Optional; blank → draft purpose.
   * @param {string} [body.typed_confirmation]  'CONFIRM' when >100 buckets.
   */
  absorbSourceRemnant: (dataSourceId, body) =>
    httpClient.post(
      `/ropa/purpose-curation/unclustered-by-source/${dataSourceId}/absorb`,
      body || {},
    ),

  /**
   * Slim list of active `ProcessingPurpose` rows for the merge-target
   * picker. Backed by the lightweight helper endpoint added with this
   * feature — see `routes_purpose_curation.py::list_processing_purposes`.
   * @param {object} [params]
   * @param {string} [params.status]  Filter (default 'active').
   * @param {string} [params.search]  Optional substring match on `name`.
   * @param {number} [params.limit]   Page size (default 100, max 500).
   */
  listProcessingPurposes: (params) =>
    httpClient.get('/ropa/purpose-curation/purposes', { params }),

  /**
   * Apply a bulk action (group_as_new | assign_to_existing | reject) to multiple proposals.
   * Selections >200 are transparently chunked server-side. Selections >100 require
   * typed_confirmation. Selections >500 are rejected with 413.
   * @param {object} body
   * @param {string} body.action  'group_as_new' | 'assign_to_existing' | 'reject'
   * @param {number[]} body.proposal_ids
   * @param {object} [body.new_purpose]
   * @param {number} [body.target_ropa_id]
   * @param {string} [body.rejection_reason]
   * @param {string} [body.typed_confirmation]
   */
  bulkAction: (body) =>
    httpClient.post('/ropa/purpose-curation/proposals/bulk', body),

  /**
   * Undo a bulk action within the 10-second window (returns 410 when expired).
   * @param {string} actionId  UUID returned by bulkAction.
   */
  undoBulkAction: (actionId) =>
    httpClient.post(`/ropa/purpose-curation/proposals/bulk/${actionId}/undo`),

  /**
   * Side-by-side evidence comparison for 2–10 proposals.
   * @param {number[]} proposalIds
   */
  compareProposals: (proposalIds) =>
    httpClient.post('/ropa/purpose-curation/proposals/compare', { proposal_ids: proposalIds }),

  /**
   * Proposals that were merged into a given ProcessingPurpose, ordered by
   * resolved_at descending.
   * @param {number} purposeId  Target ProcessingPurpose id.
   */
  getMergedProposals: (purposeId) =>
    httpClient.get(`/ropa/purpose-curation/purposes/${purposeId}/merged-proposals`),

  /**
   * Find pending proposals whose binding_signature overlaps the anchor's
   * above the similarity threshold (Spec 030 US4, FR-030-35).
   * @param {number} proposalId  Anchor proposal — must have a binding_signature.
   */
  suggestSimilar: (proposalId) =>
    httpClient.get(`/ropa/purpose-curation/proposals/${proposalId}/suggest-similar`),

  /**
   * Merge selected candidate proposals into the anchor's resolved purpose
   * as one atomic action (FR-030-38). Returns action_id for 10-second undo.
   * @param {number} proposalId  Anchor proposal id.
   * @param {object} body
   * @param {number[]} body.candidate_proposal_ids  1-50 candidate ids.
   */
  applyPropagation: (proposalId, body) =>
    httpClient.post(`/ropa/purpose-curation/proposals/${proposalId}/apply-propagation`, body),

  /**
   * Suppress the propagation banner for this proposal for the session (FR-030-39).
   * @param {number} proposalId  Anchor proposal id.
   */
  dismissPropagation: (proposalId) =>
    httpClient.post(`/ropa/purpose-curation/proposals/${proposalId}/dismiss-propagation`),

  /**
   * Revert a merged proposal back to pending status.  Un-links its
   * member-table classifications from the target purpose and trims the ROPA
   * advisory banner.
   * @param {number|string} id  Proposal id (must be in 'merged' status).
   */
  unmergeProposal: (id) =>
    httpClient.delete(`/ropa/purpose-curation/proposals/${id}/unmerge`),

  /**
   * Reset a rejected proposal back to pending status.  Clears
   * rejection_reason, resolved_at, and resolved_by so the proposal
   * re-enters the triage queue as if it had never been acted on.
   * @param {number|string} id  Proposal id (must be in 'rejected' status).
   */
  unrejectProposal: (id) =>
    httpClient.delete(`/ropa/purpose-curation/proposals/${id}/unreject`),

  /**
   * Detach a grouped proposal from its group_as_new bundle and re-approve
   * it under a new standalone purpose.  Only valid when is_group_member is
   * true (the proposal shares its resolved_purpose_id with ≥1 other approved
   * proposal).
   * @param {number|string} id  Proposal id (must be approved + is_group_member).
   */
  ungroupProposal: (id) =>
    httpClient.delete(`/ropa/purpose-curation/proposals/${id}/ungroup`),

  /**
   * Return an unclustered-absorb pending group back to unclustered status.
   * Undoes absorb_source_remnant or group_as_new on unclustered proposals,
   * resetting all group members to 'unclustered' and removing the draft purpose.
   * @param {number|string} id  Any member proposal id (must be pending + grouped-unclustered).
   */
  returnGroupToUnclustered: (id) =>
    httpClient.post(`/ropa/purpose-curation/proposals/${id}/return-to-unclustered`),

  // ── Spec 052 US1 — drift, re-confirmation, and FR-072 restructure ──────
  // Contracts: chain-core.md §2 (GET drift), §3 (reconfirm), §5 (merge),
  // §6 (split). Response envelopes are the same two-layer shape peeled by
  // `unwrap()` in usePurposeDrift.js.

  /**
   * GET the open drift event + per-column evidence detail for a purpose
   * (contracts/chain-core.md §2, FR-005). ``409 NO_OPEN_DRIFT`` when the
   * purpose has no outstanding drift to resolve.
   * @param {number|string} purposeId  ProcessingPurpose id.
   */
  getPurposeDrift: (purposeId) =>
    httpClient.get(`/ropa/purpose-curation/purposes/${purposeId}/drift`),

  /**
   * POST the DPO's single re-confirmation decision on a drifted purpose
   * (contracts/chain-core.md §3, FR-005/006). Applies the drift diff to the
   * evidence links and re-evaluates the impact-assessment obligation.
   * @param {number|string} purposeId  ProcessingPurpose id.
   * @param {object} [body]
   * @param {string}   [body.note]           Optional DPO note (≤2000 chars).
   * @param {number[]} [body.exclude_added]  classificationIds from the open
   *                                         drift's ``added`` set to release
   *                                         rather than claim.
   */
  reconfirmPurposeDrift: (purposeId, body) =>
    httpClient.post(
      `/ropa/purpose-curation/purposes/${purposeId}/reconfirm`,
      body || {},
    ),

  /**
   * POST merge ≥2 purposes into one DRAFT successor with lineage
   * (contracts/chain-core.md §5, FR-072). Predecessors + their ROPAs are
   * archived; the successor requires fresh DPO approval.
   * @param {object} body
   * @param {number[]} body.purposeIds   ≥2 purpose ids to supersede.
   * @param {string}   body.name         Successor purpose name (required).
   * @param {string}  [body.description]
   * @param {string}  [body.legalBasis]
   * @param {string}  [body.businessUnit]
   */
  mergePurposes: (body) =>
    httpClient.post('/ropa/purpose-curation/purposes/merge', body || {}),

  /**
   * POST split one purpose into ≥2 DRAFT successors with lineage
   * (contracts/chain-core.md §6, FR-072). Partitions must disjointly and
   * exactly cover the predecessor's active evidence or the server returns
   * ``422 PARTITION_INVALID`` with ``details.unassignedClassificationIds``.
   * @param {number|string} purposeId  Predecessor purpose id.
   * @param {object} body
   * @param {Array<object>} body.partitions  Named groups of classification ids.
   */
  splitPurpose: (purposeId, body) =>
    httpClient.post(
      `/ropa/purpose-curation/purposes/${purposeId}/split`,
      body || {},
    ),

  /**
   * Spec 052 T233 (FR-026, R-11) — clustering pipeline health, own-DB only.
   * Powers the honesty banner: "your data is clean" must never be
   * indistinguishable from "the step that builds your register did not run"
   * (chain-core.md §8a). Returns per-live-source clustering state without ever
   * opening a customer-DB connection.
   * @returns success_response → { data: { sources: [...], generatedAt } }
   */
  getPipelineHealth: () =>
    httpClient.get('/ropa/purpose-curation/pipeline-health'),
};
